import { doc, setDoc, getDoc } from 'firebase/firestore';
import { uploadBytes, getDownloadURL, deleteObject, uploadString, ref, listAll } from 'firebase/storage';
import { QuestionData, UIPreferences } from '../../types/questionData';
import { TestMetadata } from '../../types/testMetadata';
import { TestSession } from '../../types/whiteboardProps';
import { DynamicFirebaseService } from './dynamicFirebaseService';

export class DynamicQuestionStorageService {
  private firebaseService: DynamicFirebaseService;
  private METADATA_COLLECTION = 'testMetadata';
  private UI_PREFS_COLLECTION = 'uiPreferences';
  private DRAWING_STORAGE_PATH = 'drawings';
  private QUESTION_DATA_PATH = 'questionData';

  constructor(firebaseService: DynamicFirebaseService) {
    this.firebaseService = firebaseService;
  }

  /**
   * Create a file path for storage
   */
  private createPath(userId: number | string, tenantId: number | string, testId: string, questionId: string): string {
    return `${this.QUESTION_DATA_PATH}/${tenantId}/${userId}/${testId}/${questionId}.json`;
  }

  /**
   * Save question data to Firebase Storage
   */
  async saveQuestionData(data: QuestionData): Promise<void> {
    try {
      const path = this.createPath(data.userId, data.tenantId, data.testId, data.questionId);
      const storageRef = this.firebaseService.getStorageRef(path);
      
      // Convert data to JSON string
      const jsonData = JSON.stringify({
        ...data,
        updatedAt: Date.now()
      });
      
      // Upload as JSON to Storage
      await uploadString(storageRef, jsonData, 'raw', {
        contentType: 'application/json',
      });
      
    
    } catch (error) {
     
      throw error;
    }
  }

  /**
   * Get question data from Firebase Storage
   */
  async getQuestionData(
    userId: number | string,
    tenantId: number | string,
    testId: string,
    questionId: string
  ): Promise<QuestionData | null> {
    try {
      const path = this.createPath(userId, tenantId, testId, questionId);
      const storageRef = this.firebaseService.getStorageRef(path);
      
      const url = await getDownloadURL(storageRef);
      const response = await fetch(url);
      const data = await response.json();
      
      return data as QuestionData;
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') {
        return null;
      }
    
      return null;
    }
  }

  /**
   * Get all questions for a test from Storage
   */
  async getAllQuestionsForTest(
    userId: number | string,
    tenantId: number | string,
    testId: string
  ): Promise<Record<string, QuestionData>> {
    try {
      const result: Record<string, QuestionData> = {};
      
      // List all files in the test directory
      const testPath = `${this.QUESTION_DATA_PATH}/${tenantId}/${userId}/${testId}`;
      const listRef = this.firebaseService.getStorageRef(testPath);
      
      // This is a simplified version - in production you'd need to handle pagination
      const fileList = await listAll(listRef);
      
      // Download each file
      for (const itemRef of fileList.items) {
        try {
          const url = await getDownloadURL(itemRef);
          const response = await fetch(url);
          const data = await response.json() as QuestionData;
          result[data.questionId] = data;
        } catch (error) {
          console.error('Error downloading question data:', itemRef.name, error);
        }
      }
      
      return result;
    } catch (error) {
      console.error('Error getting all questions from Storage:', error);
      return {};
    }
  }

  /**
   * Save question data with drawing
   */
  async saveQuestionWithDrawing(
    data: QuestionData,
    drawingImageBlob?: Blob
  ): Promise<void> {
    try {
      // Save question data to Storage
      await this.saveQuestionData(data);

      // Save drawing image if provided
      if (drawingImageBlob) {
        const drawingPath = `${this.DRAWING_STORAGE_PATH}/${data.tenantId}/${data.userId}/${data.testId}/${data.questionId}.png`;
        const storageRef = this.firebaseService.getStorageRef(drawingPath);
        
        await uploadBytes(storageRef, drawingImageBlob);
        const downloadUrl = await getDownloadURL(storageRef);
        
      }
    } catch (error) {
      console.error('Error saving question with drawing:', error);
      throw error;
    }
  }

  /**
   * Save batch question data to Storage
   */
  async saveBatchQuestionData(dataArray: QuestionData[]): Promise<void> {
    try {
      // Save each question data in parallel
      const savePromises = dataArray.map(data => this.saveQuestionData(data));
      await Promise.all(savePromises);
    } catch (error) {
      console.error('Error in batch save to Storage:', error);
      throw error;
    }
  }

  /**
   * Save test metadata
   */
  async saveTestMetadata(metadata: TestMetadata): Promise<void> {
    try {
      const key = `${metadata.tenantId}_${metadata.userId}_${metadata.testId}`;
      const collectionRef = this.firebaseService.getCollection(this.METADATA_COLLECTION);
      const docRef = doc(collectionRef, key);
      
      await setDoc(docRef, {
        ...metadata,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving test metadata:', error);
      throw error;
    }
  }

  /**
   * Get test metadata
   */
  async getTestMetadata(
    userId: number | string,
    tenantId: number | string,
    testId: string
  ): Promise<TestMetadata | null> {
    try {
      const key = `${tenantId}_${userId}_${testId}`;
      const collectionRef = this.firebaseService.getCollection(this.METADATA_COLLECTION);
      const docRef = doc(collectionRef, key);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as TestMetadata;
      }
      return null;
    } catch (error) {
      console.error('Error getting test metadata:', error);
      return null;
    }
  }

  /**
   * Save UI preferences
   */
  async saveUIPreferences(
    userId: number | string,
    tenantId: number | string,
    preferences: any
  ): Promise<void> {
    try {
      const key = `${tenantId}_${userId}`;
      const collectionRef = this.firebaseService.getCollection(this.UI_PREFS_COLLECTION);
      const docRef = doc(collectionRef, key);
      
      await setDoc(docRef, {
        userId,
        tenantId,
        preferences,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving UI preferences:', error);
      throw error;
    }
  }

  /**
   * Get UI preferences
   */
  async getUIPreferences(
    userId: number | string,
    tenantId: number | string
  ): Promise<UIPreferences | null> {
    try {
      const key = `${tenantId}_${userId}`;
      const collectionRef = this.firebaseService.getCollection(this.UI_PREFS_COLLECTION);
      const docRef = doc(collectionRef, key);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as UIPreferences;
      }
      return null;
    } catch (error) {
      console.error('Error getting UI preferences:', error);
      return null;
    }
  }

  /**
   * Save test session
   */
  async saveTestSession(session: TestSession): Promise<void> {
    try {
      const key = `${session.tenantId}_${session.userId}_${session.testId}`;
      const collectionRef = this.firebaseService.getCollection('testSessions');
      const docRef = doc(collectionRef, key);
      
      // Filter out undefined values as Firebase doesn't accept them
      const cleanSession = JSON.parse(JSON.stringify(session));
      
      await setDoc(docRef, {
        ...cleanSession,
        updatedAt: Date.now()
      }, { merge: true });
    } catch (error) {
      console.error('Error saving test session:', error);
      throw error;
    }
  }

  /**
   * Get test session
   */
  async getTestSession(
    userId: string,
    tenantId: string | number,
    testId: string
  ): Promise<TestSession | null> {
    try {
      const key = `${tenantId}_${userId}_${testId}`;
      const collectionRef = this.firebaseService.getCollection('testSessions');
      const docRef = doc(collectionRef, key);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return docSnap.data() as TestSession;
      }
      return null;
    } catch (error) {
      console.error('Error getting test session:', error);
      return null;
    }
  }

  /**
   * Get latest test session (alias for getTestSession)
   */
  async getLatestTestSession(
    userId: string,
    tenantId: string | number,
    testId: string
  ): Promise<TestSession | null> {
    return this.getTestSession(userId, tenantId, testId);
  }

  /**
   * Delete drawing from storage
   */
  async deleteDrawing(
    userId: number | string,
    tenantId: number | string,
    testId: string,
    questionId: string
  ): Promise<void> {
    try {
      const drawingPath = `${this.DRAWING_STORAGE_PATH}/${tenantId}/${userId}/${testId}/${questionId}.png`;
      const storageRef = this.firebaseService.getStorageRef(drawingPath);
      
      await deleteObject(storageRef);
    } catch (error) {
      // Ignore if file doesn't exist
      if ((error as any).code !== 'storage/object-not-found') {
        console.error('Error deleting drawing:', error);
        throw error;
      }
    }
  }

  /**
   * Update selection history (not implemented for Storage version)
   */
  async updateSelectionHistory(
    userId: number | string,
    tenantId: number | string,
    testId: string,
    questionId: string,
    choice: string
  ): Promise<void> {
    // This functionality is handled through saveQuestionData
    return Promise.resolve();
  }

  
  /**
   * Static method wrapper for compatibility
   */
  static async batchSaveQuestionData(_dataArray: QuestionData[]): Promise<void> {
    throw new Error('Use instance method instead of static method');
  }
}